import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'framer-motion';
import {  Ticket } from 'lucide-react';
import { PieChart, Pie, Cell } from 'recharts';
import { GLASS_CARD } from '@/components/ui/glass';
import { useClientVouchers } from '@/components/hooks/useClientVouchers';
import Pagination from '@/components/pages/shared/Pagination';

const COLORS = ['#103254', '#E5E7EB'];

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const PAGE_SIZE_STORAGE_KEY = 'bookoll:client-bonus:pageSize';

const readStoredPageSize = () => {
  if (typeof window === 'undefined') return DEFAULT_PAGE_SIZE;
  const stored = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
  return PAGE_SIZE_OPTIONS.includes(stored) ? stored : DEFAULT_PAGE_SIZE;
};

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
};

export default function ClientBonus() {
  const usuarioId = localStorage.getItem('id_usuario') || '';
  const { vouchers, loading } = useClientVouchers(usuarioId);

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(readStoredPageSize);

  const filtered = vouchers.filter((v) => {
    const q = filter.toLowerCase();
    return (
      (v.nombre_bono || '').toLowerCase().includes(q) ||
      (v.nombre_servicio || '').toLowerCase().includes(q) ||
      (v.nombre_empresa || '').toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPageData = filtered.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
    if (typeof window !== 'undefined') {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    }
  }, [pageSize]);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="space-y-6 pb-28 lg:pb-0"
    >
      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Mis bonos
          </span>
        </div>
      </motion.div>

      {/* Filtro */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-4">
          <Input
            type="text"
            placeholder="Buscar por bono, servicio o empresa..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full sm:w-1/2 rounded-xl text-sm dark:bg-white/5 dark:border-white/10 dark:text-white"
          />
        </CardContent>
      </Card>

      {/* Lista de bonos */}
      <AnimatePresence>
        {loading ? (
          <motion.div
            variants={itemVariants}
            className="flex justify-center items-center h-32"
          >
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
          </motion.div>
        ) : filtered.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center text-gray-500 dark:text-gray-400">
              <Ticket className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {vouchers.length === 0
                  ? 'No tienes bonos comprados'
                  : 'Sin coincidencias'}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {vouchers.length === 0
                  ? 'Si quieres comprar un bono, contacta directamente con la empresa. Ellos te lo venden y aparecerá aquí.'
                  : 'No se encontraron bonos con ese criterio.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <motion.div variants={containerVariants} className="space-y-4">
              {currentPageData.map((v) => {
                const data = [
                  { name: 'Usados', value: v.usos_realizados },
                  {
                    name: 'Restantes',
                    value: v.cantidad_servicios - v.usos_realizados,
                  },
                ];

                return (
                  <motion.div key={v.id_bono_comprado} variants={itemVariants}>
                    <Card className={`rounded-2xl ${GLASS_CARD}`}>
                      <CardContent className="p-6 flex items-center gap-6">
                        <div className="flex flex-col items-center shrink-0">
                          <PieChart width={90} height={90}>
                            <Pie
                              data={data}
                              innerRadius={30}
                              outerRadius={40}
                              startAngle={90}
                              endAngle={-270}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {data.map((_, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLORS[index % COLORS.length]}
                                />
                              ))}
                            </Pie>
                          </PieChart>
                          <span className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                            {v.usos_realizados}/{v.cantidad_servicios}
                          </span>
                        </div>

                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg text-[#103254] dark:text-blue-400">
                            {v.nombre_bono}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {v.nombre_servicio}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            <span className="font-medium">Empresa:</span>{' '}
                            {v.nombre_empresa}
                          </p>

                          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400 space-y-1">
                            <p>
                              <span className="font-medium">Vencimiento:</span>{' '}
                              {v.fecha_vencimiento
                                ? new Date(
                                    v.fecha_vencimiento
                                  ).toLocaleDateString()
                                : '-'}
                            </p>
                            <p>
                              <span className="font-medium">Precio:</span>{' '}
                              {v.precio_total != null
                                ? `${v.precio_total.toFixed(2)} €`
                                : '-'}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Paginación — componente compartido */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filtered.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              itemLabel="bonos"
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
}